import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Panel, Section, StateView, useAuth } from "@ozpanel/ui";
import { StatusPill } from "@ozpanel/kit";

/**
 * EVERY SERVICE PLANE, ON EVERY NODE, IN ONE SCREEN — the transfer watch.
 *
 * During a role transfer the question is never "what does this node think": it is "is the move
 * finished everywhere, and if not, which node's which plane is holding it". Answering that used to
 * mean opening one console per node and correlating them by eye, at exactly the moment a node may
 * be unreachable and a page may be hanging on it.
 *
 * The whole matrix comes from ONE request. Each node publishes only its own rows into a replicated
 * table, so this endpoint makes no peer call and answers at the same speed with a node down as with
 * every node up — which is the only way a screen an operator stares at during a failover can be
 * trusted to keep painting.
 *
 * What it refuses to do is round a partial answer up. A stale row is what a node said BEFORE it went
 * quiet and is shown as stale, never counted as agreement; an unmeasurable plane is not agreement
 * either; a node still applying an older declaration is CONVERGING, which is reported apart from a
 * fault so the operator can tell "wait" from "act".
 */
interface PlaneRow {
  node: string;
  plane: string;
  seq: number;
  expected: string;
  action: string;
  reports?: string;
  agrees: boolean;
  /** done | failed | unmeasurable | no-master-concept */
  state: string;
  error?: string;
  age_sec: number;
  stale: boolean;
}
interface NodePlanes {
  node: string;
  /** The NODE's heartbeat liveness, which is not the same as its rows being fresh. */
  live: boolean;
  seq: number;
  complete: boolean;
  summary: string;
  planes: PlaneRow[] | null;
  /** Has this node published ANY plane state? A registered node that has not is the case a
   *  row-driven view cannot express: it was simply absent, and completion was computed without it. */
  reporting: boolean;
  /** Planes other nodes report and this one does not — typically a node left on an older build. */
  missing?: string[] | null;
  /** The build this node last reported on heartbeat. */
  version: string;
  /** Whether that build equals the release the node serving this page runs. */
  version_match: boolean;
}
interface Matrix {
  seq: number;
  declared_primary: string;
  /** Go marshals a nil slice as NULL, not []. Typing these as arrays and calling .map on them is
   *  what took the whole cluster page down with "Cannot read properties of null (reading 'map')"
   *  on a node that had not published any plane state yet — which is every node, briefly, after a
   *  deploy, and any node whose panel has not run a gate pass. */
  plane_names: string[] | null;
  nodes: NodePlanes[] | null;
  complete: boolean;
  converging?: string[] | null;
  blocking?: string[] | null;
  summary: string;
  at: string;
  /** The build the serving node runs — every other node's version is compared against it. */
  release: string;
  /** Every LIVE node reports `release`. A down node is not counted, but is named in `outdated`. */
  release_match: boolean;
  outdated?: string[] | null;
}
interface LocalTransfer {
  seq: number;
  primary: string;
  in_role: boolean;
  complete: boolean;
  summary: string;
}

function cellTone(p: PlaneRow | undefined): "ok" | "warn" | "bad" | "neutral" {
  if (!p) return "neutral";
  if (p.stale) return "warn";
  if (p.state === "done") return "ok";
  if (p.state === "no-master-concept") return "neutral";
  if (p.state === "unmeasurable") return "warn";
  return "bad";
}

/** What the cell SAYS, which is the measured fact and not the intent. */
function cellText(p: PlaneRow | undefined): string {
  if (!p) return "—";
  if (p.stale) return `stale ${p.age_sec}s`;
  if (p.state === "no-master-concept") return "n/a";
  if (p.state === "done") return p.expected === "primary" ? "primary" : "follower";
  return p.state;
}

export function PlaneMatrixPanel({ embedded = false }: { embedded?: boolean }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-planes"],
    queryFn: () => client.apiFetch<{ matrix: Matrix; local: LocalTransfer }>("/cluster/planes"),
    // While a transfer is IN FLIGHT this polls fast, because the thing being watched takes seconds
    // and a 15s refresh would show an operator a state that had already been superseded twice.
    // Once everything is in role it backs off — a settled cluster does not need 2s polling.
    refetchInterval: (query) => {
      const d = query.state.data as { matrix?: Matrix } | undefined;
      return d?.matrix?.complete ? 15000 : 2000;
    },
  });

  const raw = q.data?.matrix;
  // ONE normalisation, at the boundary. Guarding at each use site is how one of them gets missed —
  // and the one that gets missed is on the path that only occurs before anything has reported.
  const m = raw
    ? {
        ...raw,
        plane_names: raw.plane_names ?? [],
        nodes: (raw.nodes ?? []).map((n) => ({ ...n, planes: n.planes ?? [] })),
      }
    : undefined;
  if (q.isLoading || q.isError || !m) {
    return (
      <StateView
        loading={q.isLoading}
        error={q.isError ? "The plane matrix could not be read" : undefined}
        empty={!q.isLoading && !q.isError && !m}
        emptyMessage="No node has published plane state yet."
      />
    );
  }

  const popOut = () => {
    // A dedicated window an operator can leave open on a second screen through a failover, which is
    // the whole point: the transfer is watched WHILE other work happens in the main console.
    window.open(
      `${window.location.origin}${window.location.pathname}#/nodes/planes?popout=1`,
      "ozpanel-planes",
      "width=1100,height=760,menubar=no,toolbar=no",
    );
  };

  const body = (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <StatusPill tone={m.complete ? "ok" : "warn"}>
          {m.complete ? "TRANSFER COMPLETE" : "IN FLIGHT"}
        </StatusPill>
        <span>
          declaration <strong>seq {m.seq}</strong> — primary <strong>{m.declared_primary || "none"}</strong>
        </span>
        {!embedded && (
          <button onClick={popOut} style={{ marginLeft: "auto" }}>
            Pop out ↗
          </button>
        )}
      </div>

      {/* The summary is generated from the SAME numbers the table renders, so a banner can never
          contradict the row beneath it. */}
      <p style={{ margin: "0 0 12px", opacity: 0.85 }}>{m.summary}</p>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px 10px" }}>Node</th>
              <th style={{ textAlign: "left", padding: "6px 10px" }}>Seq</th>
              {/* THE BUILD, GREEN WHEN IT MATCHES. A matrix that says every plane agrees on a
                  cluster whose binaries disagree is reporting agreement about the wrong thing —
                  measured 2026-09-12, a recovery fix deployed to one node while two others sat on
                  the build with the bug, and this screen read "TRANSFER COMPLETE". */}
              <th style={{ textAlign: "left", padding: "6px 10px" }} title={`release on this node: ${m.release}`}>
                Version
              </th>
              {m.plane_names.map((p) => (
                <th key={p} style={{ textAlign: "left", padding: "6px 10px" }}>
                  {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {m.nodes.map((n) => (
              <tr key={n.node} style={{ borderTop: "1px solid rgba(128,128,128,.25)" }}>
                <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                  <StatusPill tone={n.live ? (n.complete ? "ok" : "warn") : "bad"}>
                    {n.live ? n.node : `${n.node} (dead)`}
                  </StatusPill>
                </td>
                {/* A node behind the declared seq is converging, not broken — shown as the number
                    so the operator sees the gap rather than a colour that implies a fault. */}
                <td style={{ padding: "6px 10px", opacity: n.seq < m.seq ? 1 : 0.6 }}>
                  {n.seq}
                  {n.seq < m.seq ? ` → ${m.seq}` : ""}
                </td>
                {/* A down node on an old build is amber, not red: it cannot be updated until it
                    returns, and it is named below so its return is not mistaken for convergence. */}
                <td style={{ padding: "6px 10px", whiteSpace: "nowrap", fontFamily: "monospace" }}
                    title={n.version_match ? `matches ${m.release}` : `expected ${m.release}`}>
                  <StatusPill tone={n.version_match ? "ok" : n.live ? "bad" : "warn"}>
                    {n.version || "unknown"}
                  </StatusPill>
                </td>
                {/* A node that has published nothing gets ONE spanning cell saying so, rather than
                    a row of dashes that reads as "no problems here". */}
                {!n.reporting ? (
                  <td colSpan={m.plane_names.length} style={{ padding: "6px 10px" }}>
                    <StatusPill tone="bad">not reporting</StatusPill>{" "}
                    <span style={{ opacity: 0.85 }}>{n.summary}</span>
                  </td>
                ) : null}
                {n.reporting && m.plane_names.map((pn) => {
                  const p = n.planes.find((x) => x.plane === pn);
                  return (
                    <td key={pn} style={{ padding: "6px 10px", whiteSpace: "nowrap" }} title={p?.error || p?.reports || ""}>
                      <StatusPill tone={p ? cellTone(p) : "bad"}>{p ? cellText(p) : "absent"}</StatusPill>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The fault list, named exactly: node/plane:state. An operator should never have to scan a
          grid to find which cell is the problem. */}
      {m.blocking && m.blocking.length > 0 && (
        <p style={{ marginTop: 12 }}>
          <strong>Not in role:</strong> {m.blocking.join(", ")}
        </p>
      )}
      {/* Named exactly, like the fault list: node@version. Shown whenever ANY node differs, live or
          down — a down node on an old build is the one that comes back with the bug. */}
      {m.outdated && m.outdated.length > 0 && (
        <p style={{ marginTop: 4 }}>
          <StatusPill tone={m.release_match ? "warn" : "bad"}>
            {m.release_match ? "release mismatch on down nodes" : "RELEASE MISMATCH"}
          </StatusPill>{" "}
          this node runs <code>{m.release}</code>; differs on: {m.outdated.join(", ")}
        </p>
      )}
      {m.outdated && m.outdated.length === 0 && m.release && (
        <p style={{ marginTop: 4, opacity: 0.85 }}>
          <StatusPill tone="ok">release {m.release} on every node</StatusPill>
        </p>
      )}
      {m.converging && m.converging.length > 0 && (
        <p style={{ marginTop: 4, opacity: 0.85 }}>
          <strong>Converging:</strong> {m.converging.join(", ")} — still applying the declaration.
        </p>
      )}
      <p style={{ marginTop: 10, fontSize: "0.85em", opacity: 0.7 }}>
        Each cell is what that node's daemon REPORTED after the role driver ran, not what the driver
        intended. A cell reads stale when the node has stopped publishing — that is the node's last
        observation, not a statement about now.
      </p>
    </>
  );

  if (embedded) return body;
  return (
    <Panel>
      <Section title="Role transfer — every plane, every node">{body}</Section>
    </Panel>
  );
}
